import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { SiteLayoutComponent } from './layouts/site-layout/site-layout.component';
import { AuthGuard } from './guard/auth.guard';

import { LoginComponent } from './components/login/login.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { ProfileComponent } from './components/profile/profile.component';
import { UserComponent } from './components/user/user.component';
import { UserListComponent } from './components/user/user-list/user-list.component';
import { HomeComponent } from './components/home/home.component';
import { AddUserComponent } from './components/user/add-user/add-user.component';
import { EditUserComponent } from './components/user/edit-user/edit-user.component';
import { DeviceComponent } from './components/device/device.component';
import { SettingsComponent } from './components/settings/settings.component';
import { AddDeleteDeviceComponent } from './components/device/add-delete-device/add-delete-device.component';
import { DeviceDetailsComponent } from './components/device/device-details/device-details.component';
import { HelpAndSupportComponent } from './components/help-and-support/help-and-support.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { AlertComponent } from './components/alert/alert.component';
import { AlertDetailsComponent } from './components/alert/alert-details/alert-details.component';
import { ProjectComponent } from './components/project/project.component';
import { ProjectListComponent } from './components/project/project-list/project-list.component';
import { AddProjectComponent } from './components/project/add-project/add-project.component';
import { EditProjectComponent } from './components/project/edit-project/edit-project.component';
import { NotifyComponent } from './components/notify/notify.component';
import { UserRoleEnum } from './enums/user-role-enum';
import { ChangePasswordComponent } from './components/change-password/change-password.component';

export const routes: Routes = [
  //Auth routes goes here
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      { path: '', component: LoginComponent, pathMatch: 'full' },
      { path: 'login', component: LoginComponent, title: 'Metrium' },

      { path: 'forgotpassword', component: ForgotPasswordComponent },
      { path: 'resetpassword/:token', component: ResetPasswordComponent },
      { path: 'setpassword/:token', component: ChangePasswordComponent },
    ],
  },
  {
    path: '',
    component: SiteLayoutComponent,
    children: [
      {
        path: 'home',
        component: HomeComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
        title: 'Home',
      },
      {
        path: 'device',
        component: DeviceComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
        title: 'Device',
        children: [
          { path: '', redirectTo: 'add', pathMatch: 'full' },
          {
            path: 'add',
            component: AddDeleteDeviceComponent,
            canActivate: [AuthGuard],
            data: {
              roles: [
                UserRoleEnum.MasterAdmin,
                UserRoleEnum.ProjectAdmin,
                UserRoleEnum.User,
              ],
            },
            title: 'Add Device',
          },
          {
            path: 'details',
            component: DeviceDetailsComponent,
            canActivate: [AuthGuard],
            data: {
              roles: [
                UserRoleEnum.MasterAdmin,
                UserRoleEnum.ProjectAdmin,
                UserRoleEnum.User,
              ],
            },
            title: 'Device Details',
          },
          {
            path: ':dev_eui/details',
            component: DeviceDetailsComponent,
            canActivate: [AuthGuard],
            data: {
              roles: [
                UserRoleEnum.MasterAdmin,
                UserRoleEnum.ProjectAdmin,
                UserRoleEnum.User,
              ],
            },
            title: 'Device Details',
          },
        ],
      },
      {
        path: 'help-and-support',
        component: HelpAndSupportComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
        title: 'Help And Support',
      },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
        title: 'Profile',
      },
      {
        path: 'user',
        component: UserComponent,
        data: {
          roles: [UserRoleEnum.MasterAdmin, UserRoleEnum.ProjectAdmin],
        },
        title: 'User',
        children: [
          { path: '', redirectTo: 'list', pathMatch: 'full' },
          {
            path: 'list',
            component: UserListComponent,
            title: 'User | List',
            canActivate: [AuthGuard],
            data: {
              roles: [UserRoleEnum.MasterAdmin, UserRoleEnum.ProjectAdmin],
            },
          },
          {
            path: 'create',
            component: AddUserComponent,
            title: 'User | Add',
            canActivate: [AuthGuard],
            data: {
              roles: [UserRoleEnum.MasterAdmin, UserRoleEnum.ProjectAdmin],
            },
          },
          {
            path: 'edit/:id',
            component: EditUserComponent,
            title: 'User | Edit',
            canActivate: [AuthGuard],
            data: {
              roles: [UserRoleEnum.MasterAdmin, UserRoleEnum.ProjectAdmin],
            },
          },
        ],
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
        title: 'Dashboard',
      },
      {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
        title: 'Settings',
      },
      {
        path: 'alert',
        component: AlertComponent,
        title: 'Alert',
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
        children: [
          { path: '', redirectTo: 'details', pathMatch: 'full' },
          {
            path: 'details',
            component: AlertDetailsComponent,
            title: 'Alert | Details',
            data: {
              roles: [
                UserRoleEnum.MasterAdmin,
                UserRoleEnum.ProjectAdmin,
                UserRoleEnum.User,
              ],
            },
            canActivate: [AuthGuard],
          },
          {
            path: ':consumer_id/details',
            component: AlertDetailsComponent,
            title: 'Alert | Details',
            data: {
              roles: [
                UserRoleEnum.MasterAdmin,
                UserRoleEnum.ProjectAdmin,
                UserRoleEnum.User,
              ],
            },
            canActivate: [AuthGuard],
          },
        ],
      },
      {
        path: 'project',
        component: ProjectComponent,
        canActivate: [AuthGuard],
        title: 'Project',
        data: {
          roles: [UserRoleEnum.MasterAdmin],
        },
        children: [
          { path: '', redirectTo: 'list', pathMatch: 'full' },
          {
            path: 'list',
            component: ProjectListComponent,
            canActivate: [AuthGuard],
            data: {
              roles: [UserRoleEnum.MasterAdmin],
            },
            title: 'Project | List',
          },
          {
            path: 'add',
            component: AddProjectComponent,
            canActivate: [AuthGuard],
            data: {
              roles: [UserRoleEnum.MasterAdmin],
            },
            title: 'Project | Add',
          },
          {
            path: ':id/edit',
            component: EditProjectComponent,
            canActivate: [AuthGuard],
            data: {
              roles: [UserRoleEnum.MasterAdmin],
            },
            title: 'Project | Edit',
          },
        ],
      },
      {
        path: 'notify',
        component: NotifyComponent,
        title: 'Notify Command',
        canActivate: [AuthGuard],
        data: {
          roles: [
            UserRoleEnum.MasterAdmin,
            UserRoleEnum.ProjectAdmin,
            UserRoleEnum.User,
          ],
        },
      },
    ],
  },
  { path: '**', redirectTo: '/login' }, // Fallback route
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
